import React, { useState, useEffect, useCallback } from "react";
import useHttp from "../hooks/use-http";
import storage from "../hooks/storage";

// Token lifetime as agreed with backend (seconds). Used to compute expirationTime.
const TOKEN_LIFETIME_SECONDS = 6900;
// Fire the refresh timer this long BEFORE expiry (ms). 5 minutes covers slow
// networks, browser timer drift in iframes, and one retry budget.
const REFRESH_BUFFER_MS = 300000;
// On app reload, treat the stored token as expired if less than this remains (ms).
const EXPIRY_TOLERANCE_MS = 1000;

let logoutTimer;
// Shared in-flight promise so concurrent triggers reuse one /refreshtoken.
let refreshInFlight = null;

const AuthContext = React.createContext({
  token: "",
  refreshToken: "",
  apiKey: "",
  ucid: "",
  isLoggedIn: false,
  login: (token) => {},
  logout: () => {},
  clearData: () => {},
});

// Calculate time remaining
const calculateRemainingTime = (expirationTime) => {
  const currentTime = new Date().getTime();
  const adjExpirationTime = new Date(expirationTime).getTime();

  const remainingDuration = adjExpirationTime - currentTime;

  return remainingDuration;
};

// Check if stored user token is expired or not
const retrieveStoredToken = () => {
  const storedToken = storage.get("token");
  const storedrefreshToken = storage.get("refreshtoken");
  const storedExpirationDate = storage.get("expirationTime");
  console.info(storedExpirationDate)
  const remainingTime = calculateRemainingTime(storedExpirationDate);

  if (remainingTime <= EXPIRY_TOLERANCE_MS) {
    storage.remove("token");
    storage.remove("expirationTime");
    return null;
  }

  return {
    token: storedToken,
    refreshtoken: storedrefreshToken,
    duration: remainingTime,
  };
};

export const AuthContextProvider = (props) => {
  const { isLoading, error, sendRequest } = useHttp();
  const tokenData = retrieveStoredToken();

  let initialToken = {
    token: "",
    refreshtoken: "",
    apiKey: "",
    ucid: "",
  };
  if (tokenData) {
    initialToken.token = tokenData.token;
    initialToken.refreshtoken = tokenData.refreshtoken;
  }

  const [token, setToken] = useState(initialToken.token);
  const [refreshToken, setrefreshToken] = useState(initialToken.refreshtoken);
  const [apiKey, setapiKey] = useState(initialToken.apiKey);
  const [ucid, setucid] = useState(initialToken.ucid);

  const userIsLoggedIn = !!token;

  // Get new token if valid refresh token
  const getNewtoken = (token, refreshToken) => {
    // Reuse the pending request if a refresh is already running.
    if (refreshInFlight) return refreshInFlight;

    refreshInFlight = new Promise((resolve) => {
      var request = { userToken: token, refreshToken: refreshToken };
      sendRequest(
        {
          url: "refreshtoken",
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: request,
        },
        (data) => {
          refreshInFlight = null;
          // useHttp returns { errorStatus, message } on failure — bail out.
          // BE only sends { userToken, tokenGeneratedAt } on success.
          if (!data.userToken || !data.tokenGeneratedAt) {
            resolve(null);
            return;
          }
          // If storage's refreshtoken changed while we were in flight, a
          // fresh /auth wrote a different session's tokens. Persisting our
          // (old-session userToken, new-session refreshtoken) pair would 401
          // on the next cycle — drop the response instead.
          const currentStoredRefresh = storage.get("refreshtoken");
          if (currentStoredRefresh && currentStoredRefresh !== refreshToken) {
            console.warn("Dropping stale /refreshtoken response — storage moved to a different session mid-request");
            resolve(null);
            return;
          }
          // BE doesn't rotate refreshToken; reuse the one we sent so the
          // persisted pair stays within the same session.
          loginHandler(data.userToken, data.tokenGeneratedAt, refreshToken);
          resolve(data);
        }
      );
    });
    return refreshInFlight;
  };
  const clearDataHandler = () => {
    setToken(null);
    if (logoutTimer) {
      clearTimeout(logoutTimer);
    }
    storage.remove("token");
    storage.remove("refreshtoken");
    storage.remove("expirationTime");
    storage.remove("apikey");
    storage.remove("ucid");
    storage.remove("oauth_status");
    storage.remove("oauth_user_info");
  };
  // Logout user and clear data
  const logoutHandler = useCallback(() => {
    const storedToken = storage.get("token");
    const storedRefreshToken = storage.get("refreshtoken");
    const storedExpirationDate = storage.get("expirationTime");
    const remainingTime = calculateRemainingTime(storedExpirationDate);
    if (remainingTime <= REFRESH_BUFFER_MS) {
      if (logoutTimer) {
        clearTimeout(logoutTimer);
      }
      // Don't null the token here — it still has REFRESH_BUFFER_MS of life
      // left, and flipping isLoggedIn=false during the refresh round-trip
      // would trigger a routing redirect → AuthForm → /api/auth cascade.
      getNewtoken(storedToken, storedRefreshToken);
    }
    //storage.remove("token");
    //storage.remove("refreshtoken");
    //storage.remove("expirationTime");
  }, []);

  // Set user tokens in storage
  const loginHandler = (token, tokenTime, refreshToken,apikey,ucid, userdata) => {
    setToken(token);
    setrefreshToken(refreshToken);
    const expirationTime = new Date(+(tokenTime + TOKEN_LIFETIME_SECONDS) * 1000);
    console.info(expirationTime.toISOString());
    storage.set("token", token);
    storage.set("refreshtoken", refreshToken);
    storage.set("expirationTime", expirationTime.toISOString());
    // Only overwrite apikey/ucid when explicitly provided. Refresh calls
    // loginHandler with only 3 args, so apikey/ucid are undefined there —
    // writing undefined would store the literal string "undefined".
    if (apikey !== undefined) storage.set("apikey", apikey);
    if (ucid !== undefined) storage.set("ucid", ucid);
    if(userdata){
      storage.set("userdata", userdata);
    }
    console.info(tokenTime)
    // Timer scheduling lives in useEffect — that's the single source of truth.
    // After the state setters above commit, useEffect re-runs with the new
    // tokenData and schedules the next refresh.
  };

  useEffect(() => {
    if (!token) return undefined;
    // Read expirationTime fresh from storage — don't depend on the captured
    // tokenData (which is a new object reference every render and would cause
    // this effect to re-run on unrelated re-renders, e.g. when useHttp flips
    // isLoading during the refresh round-trip).
    const storedExpirationDate = storage.get("expirationTime");
    if (!storedExpirationDate) return undefined;
    const remainingTime = calculateRemainingTime(storedExpirationDate);
    // Fire REFRESH_BUFFER_MS before expiry so refresh runs while token still has remainingTime.
    const id = setTimeout(
      logoutHandler,
      Math.max(remainingTime - REFRESH_BUFFER_MS, 0)
    );
    logoutTimer = id;
    return () => clearTimeout(id);
  }, [token, logoutHandler]);

  const contextValue = {
    token: token,
    refreshToken: refreshToken,
    isLoggedIn: userIsLoggedIn,
    apiKey: apiKey,
    ucid: ucid,
    login: loginHandler,
    logout: logoutHandler,
    clearData: clearDataHandler,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
